// Plan the Week sheet — AI-drafted meal plan.
//
// Two stages inside one modal:
//   1. Setup: free-text constraints + slot toggles → "Draft the week"
//   2. Review: 7-day proposal grid; per-cell swap/clear; "Regenerate";
//      "Apply to week" commits via mealplan.set for each populated cell
//      that the user hasn't cleared.
//
// Nothing is auto-applied — explicit user step. Existing plan entries
// the server already has are NOT replanned (server enforces this; the
// review grid also filters them out before showing proposals).

import { useEffect, useMemo, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type MealPlanEntry, type MealProposalRow, type MealSlot, type Recipe } from "../../lib/rpc";

interface Slots { breakfast: boolean; lunch: boolean; dinner: boolean }

interface PlanTheWeekProps {
  open: boolean;
  onClose: () => void;
  weekStart: string;
  weekDates: string[];
  existing: MealPlanEntry[];
  notify: (msg: string) => void;
  /** Refresh the parent's view after committing. */
  onApplied: () => Promise<void>;
}

const DEFAULT_SLOTS: Slots = { breakfast: false, lunch: false, dinner: true };

function dayName(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString([], { weekday: "short" });
}

export function PlanTheWeek({
  open, onClose, weekStart, weekDates, existing, notify, onApplied,
}: PlanTheWeekProps) {
  const [stage, setStage] = useState<"setup" | "review">("setup");
  const [constraints, setConstraints] = useState("");
  const [slots, setSlots] = useState<Slots>(DEFAULT_SLOTS);
  const [drafting, setDrafting] = useState(false);
  const [proposals, setProposals] = useState<MealProposalRow[]>([]);
  const [committing, setCommitting] = useState(false);

  // Cleared cells (user dismissed a proposed value) — keyed by
  // `${day}:${slot}`. Filter these out on apply so the user can
  // discard AI suggestions they didn't like.
  const [cleared, setCleared] = useState<Set<string>>(new Set());

  // User's saved recipes, looked up by lowercased name. On apply we
  // try to link each AI suggestion back to the matching recipe so the
  // meal-plan cell knows which recipe it is (shows the RECIPE tag and
  // can deep-link to the detail/cook view in the future). Without
  // this, the AI naming the user's "Lemon Pasta" still saves as plain
  // text with recipeId=null.
  const [recipesByName, setRecipesByName] = useState<Map<string, Recipe>>(new Map());

  // Reset state on open.
  useEffect(() => {
    if (open) {
      setStage("setup");
      setConstraints("");
      setSlots(DEFAULT_SLOTS);
      setDrafting(false);
      setProposals([]);
      setCommitting(false);
      setCleared(new Set());
    }
  }, [open]);

  // Lazy-load recipes once per open so suggestions can be linked back
  // on apply. Names compared case-insensitively + trimmed.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.recipesList()
      .then((list) => {
        if (cancelled) return;
        const m = new Map<string, Recipe>();
        for (const r of list) {
          if (typeof r.name === "string" && r.name.trim()) {
            m.set(r.name.trim().toLowerCase(), r);
          }
        }
        setRecipesByName(m);
      })
      .catch(() => { /* recipes are best-effort; falling back to free-form is OK */ });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const currentPlanForRpc = useMemo(() => {
    return existing.map((e) => {
      const idx = weekDates.indexOf(e.date);
      if (idx < 0) return null;
      return { day: idx, slot: e.slot, value: e.value };
    }).filter(Boolean) as Array<{ day: number; slot: MealSlot; value: string }>;
  }, [existing, weekDates]);

  const draftWeek = async () => {
    setDrafting(true);
    setCleared(new Set());
    try {
      const r = await api.mealplanAi({
        weekStart,
        constraints,
        slots,
        currentPlan: currentPlanForRpc,
      });
      setProposals(r.proposals);
      setStage("review");
      if (r.dropped > 0) {
        notify(`Skipped ${r.dropped} invented suggestion${r.dropped === 1 ? "" : "s"} — only saved recipes are used.`);
      }
    } catch (e: any) {
      notify(e?.message || "couldn't draft the week");
    } finally {
      setDrafting(false);
    }
  };

  // Hide proposals that collide with the user's already-set entries —
  // the server is told not to plan over them but defence-in-depth.
  const isAlreadySet = (day: number, slot: MealSlot) => {
    const date = weekDates[day];
    return existing.some((e) => e.date === date && e.slot === slot);
  };

  const apply = async () => {
    setCommitting(true);
    try {
      const ops: Promise<unknown>[] = [];
      let linkedCount = 0;
      for (const row of proposals) {
        for (const slot of ["breakfast", "lunch", "dinner"] as const) {
          const value = row[slot];
          if (!value) continue;
          if (cleared.has(`${row.day}:${slot}`)) continue;
          if (isAlreadySet(row.day, slot)) continue;
          const date = weekDates[row.day];
          if (!date) continue;
          // Look up an exact (case-insensitive) name match against the
          // user's saved recipes so the meal-plan cell can link to it.
          // If no match (free-form note like "Leftovers"), save with
          // recipeId=null — same shape as a manual Quick-note entry.
          const matched = recipesByName.get(value.trim().toLowerCase());
          if (matched) linkedCount++;
          ops.push(api.mealplanSet({
            date, slot, value,
            recipeId: matched ? matched.id : null,
          }));
        }
      }
      await Promise.all(ops);
      await onApplied();
      const linkedStr = linkedCount > 0 ? ` (${linkedCount} linked to recipes)` : "";
      notify(`Applied ${ops.length} meal${ops.length === 1 ? "" : "s"}${linkedStr}`);
      onClose();
    } catch (e: any) {
      notify(e?.message || "apply failed");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="ptw-bg" onClick={onClose}>
      <div className="ptw-modal" onClick={(e) => e.stopPropagation()} role="dialog">
        <header className="ptw-head">
          {stage === "review" && (
            <button className="icon-btn" onClick={() => setStage("setup")} title="Back" type="button">
              <Icon name="arrow-left" size={13} />
            </button>
          )}
          <span className="ptw-head-icon"><Icon name="sparkle" size={14} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ptw-head-title">
              {stage === "setup" ? "Plan the week" : "Review the draft"}
            </div>
            <div className="ptw-head-sub">
              {stage === "setup" ? "AI will sketch the week. You review before applying."
                : "Each suggestion has an × — clear what you don't want, then apply."}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Close" type="button">
            <Icon name="x" size={13} />
          </button>
        </header>

        {stage === "setup" ? (
          <div className="ptw-body">
            <div>
              <div className="am-label">Constraints</div>
              <textarea
                className="am-input ptw-constraints"
                rows={4}
                placeholder={"e.g. \"veg on Tuesdays, fast nights Mon/Tue, leftovers Wed, something seafood once\""}
                value={constraints}
                onInput={(e) => setConstraints((e.currentTarget as HTMLTextAreaElement).value)}
                maxLength={2000}
                autofocus
              />
            </div>

            <div>
              <div className="am-label">Slots to plan</div>
              <div className="ptw-slot-toggles">
                {(["breakfast", "lunch", "dinner"] as const).map((s) => (
                  <button
                    key={s}
                    data-active={slots[s]}
                    onClick={() => setSlots((p) => ({ ...p, [s]: !p[s] }))}
                    type="button"
                  >
                    {s[0].toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
              <p className="ptw-hint">
                Suggestions are picked from your saved recipes only — no invented dishes. Already-filled cells are never overwritten.
              </p>
            </div>

            <div className="am-actions">
              <button className="btn-ghost" onClick={onClose} type="button">Cancel</button>
              <button
                className="btn btn-primary"
                onClick={draftWeek}
                disabled={drafting || (!slots.breakfast && !slots.lunch && !slots.dinner)}
                type="button"
              >
                <Icon name="sparkle" size={13} />
                <span>{drafting ? "Drafting…" : "Draft the week"}</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="ptw-body ptw-review">
            <div className="ptw-grid">
              {weekDates.map((iso, dayIdx) => {
                const row = proposals.find((p) => p.day === dayIdx);
                return (
                  <div className="ptw-row" key={iso}>
                    <div className="ptw-day">{dayName(iso)}</div>
                    {(["breakfast", "lunch", "dinner"] as const).map((slot) => {
                      const value = row && row[slot];
                      const collides = isAlreadySet(dayIdx, slot);
                      const isCleared = cleared.has(`${dayIdx}:${slot}`);
                      if (collides) {
                        return (
                          <div className="ptw-cell ptw-cell-locked" key={slot} title="Already in your plan">
                            <span className="ptw-cell-lock">on plan</span>
                          </div>
                        );
                      }
                      if (!value || isCleared) {
                        return <div className="ptw-cell ptw-cell-empty" key={slot}>—</div>;
                      }
                      const linked = recipesByName.has(value.trim().toLowerCase());
                      return (
                        <div className="ptw-cell" key={slot} data-linked={linked}>
                          <span className="ptw-cell-value">{value}</span>
                          {linked && (
                            <span className="ptw-cell-tag" title="Matches one of your recipes — will be linked on apply">
                              recipe
                            </span>
                          )}
                          <button
                            className="ptw-cell-x"
                            onClick={() => setCleared((p) => new Set(p).add(`${dayIdx}:${slot}`))}
                            title="Clear suggestion"
                            type="button"
                          >
                            <Icon name="x" size={10} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            <div className="ptw-review-actions">
              <button
                className="btn"
                onClick={draftWeek}
                disabled={drafting || committing}
                type="button"
              >
                <Icon name="sparkle" size={13} />
                <span>{drafting ? "Regenerating…" : "Regenerate"}</span>
              </button>
              <span style={{ flex: 1 }} />
              <button
                className="btn-ghost"
                onClick={() => setStage("setup")}
                disabled={committing}
                type="button"
              >
                Edit constraints
              </button>
              <button
                className="btn btn-primary"
                onClick={apply}
                disabled={committing || drafting || proposals.length === 0}
                type="button"
              >
                <Icon name="check" size={13} />
                <span>{committing ? "Applying…" : "Apply to week"}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
